import { useFormatoNoBlur } from '@/hooks/use-formato-no-blur';
import { Label } from '@/components/ui/label';
import { decimalDigitado } from '@/lib/format';
import type { FormOrcamento, ModoFrete } from './tipos';

const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';

export interface FreteOrcamentoProps {
  form: FormOrcamento;
  set: <K extends keyof FormOrcamento>(campo: K, valor: FormOrcamento[K]) => void;
  editavel: boolean;
  /** Tem custo.ver: so quem tem informa e ve o custo do frete (a API omite o valor dos demais). Padrao: sim. */
  comCusto?: boolean;
}

function CampoMoeda({ id, rotulo, valor, onChange, disabled, prefixo = 'R$' }: { id: string; rotulo: string; valor: string; onChange: (v: string) => void; disabled: boolean; prefixo?: string }) {
  const formatado = useFormatoNoBlur(valor, 'dinheiro');
  const invalido = valor.trim() !== '' && decimalDigitado(valor) === null;
  return (
    <div>
      <Label htmlFor={id} className={ROTULO}>
        {rotulo}
      </Label>
      <div className="flex h-10 items-center gap-1.5 rounded-[10px] border border-input bg-card px-3 focus-within:border-primary focus-within:shadow-focus">
        <span aria-hidden="true" className="text-ink-muted">
          {prefixo}
        </span>
        <input
          id={id}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          placeholder="0,00"
          {...formatado}
          disabled={disabled}
          aria-invalid={invalido || undefined}
          onChange={(e) => onChange(e.target.value)}
          className="tabular w-full border-0 bg-transparent font-mono outline-none disabled:opacity-60"
        />
      </div>
      {invalido && (
        <p role="alert" className="mt-1 text-caption text-danger-ink">
          Informe um valor como 50,00.
        </p>
      )}
    </div>
  );
}

const CAIXA = 'flex cursor-pointer items-center gap-2.5';
const CAIXA_INPUT = 'size-[18px] accent-primary';

/**
 * Frete do orcamento. Modalidade: forma antiga (receita/custo em R$), CIF (eu pago o frete) ou FOB (o cliente paga).
 * Nada aqui calcula: o frete net, a MC e o lucro vem da API. Os campos de custo so existem com custo.ver.
 */
export function FreteOrcamento({ form, set, editavel, comCusto = true }: FreteOrcamentoProps) {
  const antiga = form.freteModo === 'ANTIGA';
  const cif = form.freteModo === 'CIF';

  // Escolher CIF marca "considerar frete no lucro" por padrao (o usuario pode desmarcar).
  const escolherModo = (modo: ModoFrete) => {
    set('freteModo', modo);
    if (modo === 'CIF') set('consideraFreteLucro', true);
  };

  return (
    <section aria-label="Frete" className="flex flex-col gap-3.5 rounded-2xl border border-border bg-card px-5 py-4">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] items-end gap-3.5">
        <div>
          <Label htmlFor="o-fm" className={ROTULO}>
            Modalidade do frete
          </Label>
          <select
            id="o-fm"
            value={form.freteModo}
            disabled={!editavel}
            onChange={(e) => escolherModo(e.target.value as ModoFrete)}
            className="h-10 w-full rounded-[10px] border border-input bg-card px-3 text-small outline-none focus:border-primary focus:shadow-focus disabled:opacity-60"
          >
            <option value="ANTIGA">Sem frete detalhado</option>
            <option value="CIF">CIF (eu pago o frete)</option>
            <option value="FOB">FOB (o cliente paga)</option>
          </select>
        </div>

        {antiga && (
          <>
            <CampoMoeda id="o-fr" rotulo="Receita com frete" valor={form.freteReceita} onChange={(v) => set('freteReceita', v)} disabled={!editavel} />
            {comCusto && <CampoMoeda id="o-fc" rotulo="Custo com frete" valor={form.freteCusto} onChange={(v) => set('freteCusto', v)} disabled={!editavel} />}
          </>
        )}

        {!antiga && comCusto && (
          <div>
            <Label htmlFor="o-ftr" className={ROTULO}>
              Transportadora
            </Label>
            <input
              id="o-ftr"
              type="text"
              autoComplete="off"
              maxLength={120}
              placeholder="Opcional"
              value={form.freteTransportadora}
              disabled={!editavel}
              onChange={(e) => set('freteTransportadora', e.target.value)}
              className="h-10 w-full rounded-[10px] border border-input bg-card px-3 text-small outline-none focus:border-primary focus:shadow-focus disabled:opacity-60"
            />
          </div>
        )}

        {cif && comCusto && (
          <>
            <fieldset className="flex flex-col gap-1.5">
              <legend className={ROTULO}>Base do frete</legend>
              <label className={CAIXA}>
                <input type="radio" name="o-fb" value="TOTAL" checked={form.freteBase === 'TOTAL'} disabled={!editavel} onChange={() => set('freteBase', 'TOTAL')} className={CAIXA_INPUT} />
                Valor total (R$)
              </label>
              <label className={CAIXA}>
                <input type="radio" name="o-fb" value="POR_T" checked={form.freteBase === 'POR_T'} disabled={!editavel} onChange={() => set('freteBase', 'POR_T')} className={CAIXA_INPUT} />
                R$ por t
              </label>
            </fieldset>
            {form.freteBase === 'TOTAL' ? (
              <CampoMoeda id="o-ft" rotulo="Valor do frete" valor={form.freteTotal} onChange={(v) => set('freteTotal', v)} disabled={!editavel} />
            ) : (
              <CampoMoeda id="o-ft" rotulo="Valor do frete por t" valor={form.fretePorT} onChange={(v) => set('fretePorT', v)} disabled={!editavel} prefixo="R$/t" />
            )}
          </>
        )}
      </div>

      {cif && (
        <div className="flex flex-col gap-2.5">
          {comCusto && (
            <label htmlFor="o-fcr" className={CAIXA}>
              <input id="o-fcr" type="checkbox" checked={form.freteCreditaImpostos} disabled={!editavel} onChange={(e) => set('freteCreditaImpostos', e.target.checked)} className={CAIXA_INPUT} />
              A transportadora gera crédito de ICMS/PIS/COFINS
            </label>
          )}
          <label htmlFor="o-fd" className={CAIXA}>
            <input id="o-fd" type="checkbox" checked={form.freteDestacado} disabled={!editavel} onChange={(e) => set('freteDestacado', e.target.checked)} className={CAIXA_INPUT} />
            Destacar o frete na proposta (cobrar em linha própria)
          </label>
          {form.freteDestacado ? (
            comCusto && <CampoMoeda id="o-fco" rotulo="Valor cobrado do cliente (opcional)" valor={form.freteCobrado} onChange={(v) => set('freteCobrado', v)} disabled={!editavel} />
          ) : (
            <p className="text-caption text-ink-muted">Padrão: o frete vai embutido no preço, sem linha própria.</p>
          )}
        </div>
      )}

      <label htmlFor="o-fl" className={CAIXA}>
        <input id="o-fl" type="checkbox" checked={form.consideraFreteLucro} disabled={!editavel} onChange={(e) => set('consideraFreteLucro', e.target.checked)} className={CAIXA_INPUT} />
        Considerar frete no lucro
      </label>
    </section>
  );
}
